// src/pages/financials/TimeCardsTab.tsx
//
// Time Cards — weekly time-card detail from the qbd-timecards recipe.
// Date-range inputs (default: last 28 days) + department select (options
// from the response's `depts`, plus All). Summary tiles, a detail table
// with sticky Employee column and a footer totals row, and CSV export.
// Hours render with 1 decimal; currency whole dollars; OT cells amber
// when > 0.
//
// weekStart is a 'yyyy-MM-dd' string from the agent — rendered as-is,
// never through new Date().

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, RefreshCw, AlertTriangle, Download, FileSpreadsheet } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import DateField from '../../components/DateField';
import type { QbdTimecardRow } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtHrs = (v: number) => new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v);

const pad = (n: number) => String(n).padStart(2, '0');
const dateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = () => dateStr(new Date());
const daysBack = (days: number) => { const d = new Date(); d.setDate(d.getDate() - days); return dateStr(d); };

function exportTimecards(rows: QbdTimecardRow[], exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<QbdTimecardRow> = [
    ['Week',        (r) => r.weekStart],
    ['Employee',    (r) => r.name],
    ['Dept',        (r) => r.dept],
    ['Hourly Rate', (r) => r.hourlyRate],
    ['Reg Hours',   (r) => r.regHours],
    ['OT Hours',    (r) => r.otHours],
    ['Total Hours', (r) => r.totalHours],
    ['Reg Pay',     (r) => r.regPay],
    ['OT Pay',      (r) => r.otPay],
    ['Total Pay',   (r) => r.totalPay],
  ];
  exporter('time-cards', cols, rows);
}

export function TimeCardsTab() {
  const [dateFrom, setDateFrom] = useState(() => daysBack(28));
  const [dateTo, setDateTo] = useState(todayStr);
  const [dept, setDept] = useState('');

  const q = useQuery({
    queryKey: ['qbd', 'timecards', dateFrom, dateTo, dept],
    queryFn: () => api.getQbdTimecards({
      dateFrom: dateFrom || undefined,
      dateTo:   dateTo || undefined,
      dept:     dept || undefined,
    }),
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const depts = data?.depts ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Clock size={20} /> Time Cards
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Weekly time cards from QuickBooks Desktop — hours and pay by employee and week.
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Date Range</span>
          <div className="flex items-center gap-1.5">
            <DateField value={dateFrom} onChange={setDateFrom} className="w-[130px]" />
            <span className="text-slate-600 text-xs">→</span>
            <DateField value={dateTo} onChange={setDateTo} className="w-[130px]" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Department</span>
          <select value={dept} onChange={e => setDept(e.target.value)}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 min-w-[130px]">
            <option value="">All departments</option>
            {depts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="flex-1" />
        <button
          onClick={() => exportTimecards(rows)}
          disabled={rows.length === 0}
          title="Export the time cards to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> Export
        </button>
        <button
          onClick={() => exportTimecards(rows, exportXlsx)}
          disabled={rows.length === 0}
          title="Export to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the time cards</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <>
          {/* Summary tiles */}
          {totals && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Total Hours</p>
                <p className="text-lg font-bold text-white tabular-nums">{fmtHrs(totals.regHours + totals.otHours)}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">OT Hours</p>
                <p className={`text-lg font-bold tabular-nums ${totals.otHours > 0 ? 'text-amber-400' : 'text-white'}`}>{fmtHrs(totals.otHours)}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">Total Pay</p>
                <p className="text-lg font-bold text-white tabular-nums">{fmtCur(totals.totalPay)}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/30">
                <p className="text-xs text-slate-500">OT Pay</p>
                <p className={`text-lg font-bold tabular-nums ${totals.otPay > 0 ? 'text-amber-400' : 'text-white'}`}>{fmtCur(totals.otPay)}</p>
              </div>
            </div>
          )}

          {/* Detail table */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="px-3 py-2.5 text-left text-slate-400 font-medium whitespace-nowrap">Week</th>
                    <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">Employee</th>
                    <th className="px-3 py-2.5 text-left text-slate-400 font-medium">Dept</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Rate</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Reg Hrs</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">OT Hrs</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Total Hrs</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Reg Pay</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">OT Pay</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Total Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.weekStart}-${r.empId}`} className="group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors">
                      <td className="px-3 py-2 text-slate-400 whitespace-nowrap tabular-nums">{r.weekStart}</td>
                      <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-slate-200 whitespace-nowrap">{r.name}</td>
                      <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.dept}</td>
                      <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur(r.hourlyRate)}</td>
                      <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtHrs(r.regHours)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.otHours > 0 ? 'text-amber-400' : 'text-slate-300'}`}>{fmtHrs(r.otHours)}</td>
                      <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtHrs(r.totalHours)}</td>
                      <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur(r.regPay)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.otPay > 0 ? 'text-amber-400' : 'text-slate-300'}`}>{fmtCur(r.otPay)}</td>
                      <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.totalPay)}</td>
                    </tr>
                  ))}
                  {totals && rows.length > 0 && (
                    <tr className="border-t-2 border-slate-500/60 bg-slate-800/60">
                      <td className="px-3 py-2.5 font-bold text-white whitespace-nowrap">Total</td>
                      <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5" />
                      <td className="px-3 py-2.5" />
                      <td className="px-3 py-2.5" />
                      <td className="px-3 py-2.5 text-right font-semibold text-white tabular-nums">{fmtHrs(totals.regHours)}</td>
                      <td className={`px-3 py-2.5 text-right font-semibold tabular-nums ${totals.otHours > 0 ? 'text-amber-400' : 'text-white'}`}>{fmtHrs(totals.otHours)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-white tabular-nums">{fmtHrs(totals.regHours + totals.otHours)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-white tabular-nums">{fmtCur(totals.regPay)}</td>
                      <td className={`px-3 py-2.5 text-right font-semibold tabular-nums ${totals.otPay > 0 ? 'text-amber-400' : 'text-white'}`}>{fmtCur(totals.otPay)}</td>
                      <td className="px-3 py-2.5 text-right font-semibold text-white tabular-nums">{fmtCur(totals.totalPay)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {rows.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">No time cards in the selected range</div>
            )}
            <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
              {data.dateFrom} → {data.dateTo} · {rows.length} row{rows.length === 1 ? '' : 's'}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
